import { RequestError } from "@ers/errors";
import { history } from "@ers/helpers";
import {
	getJson,
	isStatusOK,
	isValidStatus,
} from "@ers/helpers/api/ResponseProcessors";
import { getAccessToken } from "@ers/root/App.selectors";
import store from "@ers/root/App.store";
import { formatErrorMessage } from "../Errors";

const GET_CACHE_HEADERS = {
	Pragma: "no-cache",
	"Cache-Control": "no-cache, no-store",
};

class BaseClient {
	constructor(rootUrl) {
		this.rootUrl = rootUrl;
	}

	format(url) {
		return `${this.rootUrl}${url}`;
	}

	authHeaders() {
		const { getState } = store;
		const token = getAccessToken(getState());
		return { Authorization: `Bearer ${token}` };
	}

	execute(
		method,
		url,
		{ body, postprocess = isStatusOK, validStatuses, asBlob = false } = {},
	) {
		const formattedUrl = this.format(url);
		return this.request(method, formattedUrl, {
			body,
			validateStatus: isValidStatus(validStatuses),
			asBlob,
		})
			.then(postprocess)
			.catch(createError(formattedUrl));
	}

	request(
		method,
		url,
		{ body, validateStatus = isValidStatus(), asBlob = false } = {},
	) {
		const requestHeaders = this.authHeaders();
		if (method === "GET" || method === "HEAD") {
			Object.assign(requestHeaders, GET_CACHE_HEADERS);
		}

		const init = {
			method,
			headers: requestHeaders,
			credentials: "same-origin",
		};
		if (body !== undefined && body !== null) {
			if (
				typeof body === "string" ||
				body instanceof FormData ||
				body instanceof Blob
			) {
				init.body = body;
			} else {
				requestHeaders["Content-Type"] = "application/json";
				init.body = JSON.stringify(body);
			}
		}

		return fetch(url, init)
			.then(async (response) => {
				const data = asBlob
					? await response.blob()
					: parseBody(await response.text());
				const normalized = {
					data,
					status: response.status,
					statusText: response.statusText,
					headers: headersToObject(response.headers),
					config: { method, url },
				};
				if (!validateStatus(response.status)) {
					const httpError = new Error(
						`Request failed with status code ${response.status}`,
					);
					httpError.response = normalized;
					httpError.isHttpError = true;
					throw httpError;
				}
				return normalized;
			})
			.catch((error) => {
				if (error.isHttpError) {
					throw error;
				}
				const httpError = new Error(error.message);
				httpError.request = error.message;
				httpError.isHttpError = true;
				throw httpError;
			});
	}

	get(url, postprocess = getJson) {
		return this.execute("GET", url, { postprocess });
	}

	fetchFile(url, postprocess = getJson) {
		return this.execute("GET", url, { postprocess, asBlob: true });
	}

	patch(url, body, postprocess = isStatusOK, validStatuses) {
		return this.execute("PATCH", url, { body, postprocess, validStatuses });
	}

	delete(url, postprocess = isStatusOK, validStatuses) {
		return this.execute("DELETE", url, { postprocess, validStatuses });
	}

	post(url, body, postprocess = isStatusOK, validStatuses) {
		return this.execute("POST", url, { body, postprocess, validStatuses });
	}

	head(url, postprocess = isStatusOK) {
		return this.execute("HEAD", url, { postprocess });
	}
}

export default BaseClient;

function parseBody(text) {
	try {
		return JSON.parse(text);
	} catch (e) {
		return text;
	}
}

function headersToObject(headers) {
	const result = {};
	headers.forEach((value, key) => {
		result[key] = value;
	});
	return result;
}

const createError = (url) => (e) => {
	if (e.isHttpError) {
		return new RequestError(e, url);
	}
	history.goToError();
	// eslint-disable-next-line no-console
	console.error(...formatErrorMessage(e.message));
	return new Error(e);
};
